import React, { useCallback, useEffect, useState } from 'react';
import { photosApi, PHOTO_CATEGORIES, PHOTO_SIZES, CATEGORY_LABEL } from '../../services/photosApi';
import { getToken } from '../auth';
import '../styles/Galerie.css';
import './admin-form.css';

const EMPTY_FORM = {
  title: '', description: '',
  category: 'quotidien', size: 's1',
  author: '', collection: '',
  imageSeed: '', imageUrl: '',
  featured: false, published: true,
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

const Galerie = () => {
  const token = getToken();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterFeatured, setFilterFeatured] = useState('all');
  const [query, setQuery] = useState('');
  const [view, setView] = useState('grid'); // 'grid' | 'table'

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await photosApi.adminList(token, {
        category: filterCategory !== 'all' ? filterCategory : undefined,
        featured: filterFeatured === 'yes' ? true : undefined,
        q: query || undefined,
        limit: 300,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, filterCategory, filterFeatured, query]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setSaveError(null); setOpen(true); };

  const openEdit = async (id) => {
    setEditingId(id);
    setSaveError(null);
    try {
      const { photo } = await photosApi.adminGet(token, id);
      setForm({
        title: photo.title,
        description: photo.description || '',
        category: photo.category,
        size: photo.size,
        author: photo.author || '',
        collection: photo.collection || '',
        imageSeed: photo.imageSeed || '',
        imageUrl: photo.imageUrl || '',
        featured: photo.featured,
        published: photo.published,
      });
      setOpen(true);
    } catch (err) {
      alert('Impossible de charger la photo : ' + err.message);
    }
  };

  const close = useCallback(() => {
    setOpen(false); setEditingId(null); setForm(EMPTY_FORM); setSaveError(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = ''; window.removeEventListener('keydown', onKey); };
  }, [open, close]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        category: form.category,
        size: form.size,
        author: form.author,
        collection: form.collection,
        imageSeed: form.imageSeed,
        imageUrl: form.imageUrl,
        featured: form.featured,
        published: form.published,
      };
      if (editingId) await photosApi.adminUpdate(token, editingId, payload);
      else           await photosApi.adminCreate(token, payload);
      close();
      await load();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id, title) => {
    if (!window.confirm(`Supprimer définitivement « ${title} » ?`)) return;
    try {
      await photosApi.adminDelete(token, id);
      await load();
    } catch (err) {
      alert('Suppression impossible : ' + err.message);
    }
  };

  const onToggleFeatured  = async (id, v) => { try { await photosApi.adminUpdate(token, id, { featured:  !v }); load(); } catch (e) { alert(e.message); } };
  const onTogglePublished = async (id, v) => { try { await photosApi.adminUpdate(token, id, { published: !v }); load(); } catch (e) { alert(e.message); } };

  if (!token) {
    return (
      <div className="content" style={{ padding: 40 }}>
        <p style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', color: 'var(--char-500)' }}>
          Session expirée. Reconnectez-vous.
        </p>
      </div>
    );
  }

  const imgFor = (p) => p.imageUrl || `https://picsum.photos/seed/${p.imageSeed || `cb-photo-${p.id}`}/600/450`;

  return (
    <>
      <div className="topbar">
        <div className="breadcrumb">
          <span>Cambérène</span><span className="sep">/</span>
          <span>Admin</span><span className="sep">/</span>
          <b>Galerie</b>
        </div>
        <div className="search">
          <span className="ic" />
          <input
            type="text"
            placeholder="Chercher une photo, un photographe…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <kbd>⌘ K</kbd>
        </div>
        <div className="top-actions">
          <button className="icon-btn" title="Rafraîchir" onClick={load} aria-label="Rafraîchir">↻</button>
          <button className="top-cta" onClick={openCreate}>
            <span className="plus" /> Ajouter
          </button>
        </div>
      </div>

      <div className="content">
        <div className="page-head">
          <div>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.22em', textTransform: 'uppercase', color: 'var(--char-500)' }}>
              Galerie · {total} cliché{total > 1 ? 's' : ''}
            </span>
            <h1>La galerie,<br /><span style={{ fontStyle: 'italic' }}>en gestion.</span></h1>
            <p className="sub">Ajouter, mettre en avant, classer dans une collection, publier ou retirer. Tout est synchronisé avec /galerie.</p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="seg" style={{ padding: 3 }}>
              <button className={view === 'grid' ? 'on' : ''} onClick={() => setView('grid')}>Vignettes</button>
              <button className={view === 'table' ? 'on' : ''} onClick={() => setView('table')}>Tableau</button>
            </div>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="pill-btn"
              style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: '7px 14px' }}
            >
              {PHOTO_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.lbl}</option>)}
            </select>
            <select
              value={filterFeatured}
              onChange={(e) => setFilterFeatured(e.target.value)}
              className="pill-btn"
              style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: '7px 14px' }}
            >
              <option value="all">Tous</option>
              <option value="yes">★ Vedettes</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="bulkbar on" style={{ background: 'rgba(230,57,70,.12)', color: '#B72333' }}>
            <span>⚠</span><span>Erreur : {error}</span>
          </div>
        )}

        {/* VUE VIGNETTES */}
        {view === 'grid' && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 14,
          }}>
            {loading && (
              <p style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px 0', color: 'var(--char-500)', fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', textTransform: 'uppercase' }}>
                Chargement…
              </p>
            )}
            {!loading && items.length === 0 && (
              <p style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px 0', color: 'var(--char-500)' }}>
                Aucune photo. Cliquez « Ajouter » pour commencer.
              </p>
            )}
            {items.map((p) => (
              <div
                key={p.id}
                onClick={() => openEdit(p.id)}
                style={{
                  position: 'relative', borderRadius: 12, overflow: 'hidden',
                  aspectRatio: '4 / 3', cursor: 'pointer',
                  background: `url('${imgFor(p)}') center/cover no-repeat #222`,
                  boxShadow: '0 4px 18px rgba(20,17,15,.06)',
                  transition: 'transform .2s ease, box-shadow .2s ease',
                }}
              >
                {/* Overlay infos */}
                <div style={{
                  position: 'absolute', inset: 0,
                  background: 'linear-gradient(180deg, rgba(20,17,15,0) 40%, rgba(20,17,15,.85) 100%)',
                }} />
                {/* Badges top */}
                <div style={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{
                    fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase',
                    padding: '4px 7px', borderRadius: 5,
                    background: 'rgba(250,246,238,.94)', color: 'var(--char-900)',
                  }}>
                    {CATEGORY_LABEL[p.category] || p.category}
                  </span>
                  {p.featured && (
                    <span style={{
                      fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase',
                      padding: '4px 7px', borderRadius: 5,
                      background: 'var(--sunset)', color: 'var(--bone)',
                    }}>★</span>
                  )}
                  {!p.published && (
                    <span style={{
                      fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase',
                      padding: '4px 7px', borderRadius: 5,
                      background: 'rgba(20,17,15,.7)', color: 'var(--bone)',
                    }}>Caché</span>
                  )}
                </div>
                {/* Actions top-right */}
                <div
                  style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 4 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => onToggleFeatured(p.id, p.featured)}
                    title={p.featured ? 'Retirer star' : 'Mettre en vedette'}
                    style={{
                      width: 28, height: 28, borderRadius: 6,
                      background: p.featured ? 'var(--sunset)' : 'rgba(20,17,15,.65)',
                      color: 'var(--bone)', border: 0, cursor: 'pointer', fontSize: 12,
                    }}
                  >★</button>
                  <button
                    onClick={() => onDelete(p.id, p.title)}
                    title="Supprimer"
                    style={{
                      width: 28, height: 28, borderRadius: 6,
                      background: 'rgba(20,17,15,.65)', color: 'var(--bone)',
                      border: 0, cursor: 'pointer', fontSize: 12,
                    }}
                  >✕</button>
                </div>
                {/* Caption bottom */}
                <div style={{ position: 'absolute', bottom: 10, left: 12, right: 12, color: 'var(--bone)' }}>
                  <p style={{
                    fontFamily: 'var(--sans)', fontSize: 13, lineHeight: 1.3,
                    margin: 0, fontWeight: 500,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {p.title}
                  </p>
                  <small style={{
                    display: 'block', marginTop: 3,
                    fontFamily: 'var(--mono)', fontSize: 9.5, letterSpacing: '.14em',
                    color: 'rgba(250,246,238,.7)',
                  }}>
                    © {p.author} · {p.size}
                    {p.collection && ` · ${p.collection}`}
                  </small>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* VUE TABLEAU */}
        {view === 'table' && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 60 }} />
                  <th>Titre</th>
                  <th>Catégorie</th>
                  <th>Auteur</th>
                  <th>Collection</th>
                  <th>Taille</th>
                  <th>Statut</th>
                  <th>MAJ</th>
                  <th style={{ width: 110 }} />
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={9} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--char-500)', fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', textTransform: 'uppercase' }}>Chargement…</td></tr>
                )}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={9} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--char-500)' }}>
                    Aucune photo.
                  </td></tr>
                )}
                {items.map((p) => (
                  <tr key={p.id} onClick={() => openEdit(p.id)}>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div style={{ width: 48, height: 36, borderRadius: 6, backgroundImage: `url('${imgFor(p)}')`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                    </td>
                    <td><b style={{ fontWeight: 500, fontSize: 14 }}>{p.title}</b></td>
                    <td><span className="cat-pill">{CATEGORY_LABEL[p.category] || p.category}</span></td>
                    <td>{p.author}</td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--char-500)' }}>{p.collection || '—'}</td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>{p.size}</td>
                    <td>
                      <span
                        className={`status-pill ${p.published ? 'live' : 'muted'}`}
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => { e.stopPropagation(); onTogglePublished(p.id, p.published); }}
                      >
                        {p.published ? 'Publié' : 'Caché'}
                      </span>
                      {p.featured && <span style={{ marginLeft: 6, color: 'var(--sunset)' }}>★</span>}
                    </td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--char-500)' }}>{fmtDate(p.updatedAt)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="row-actions" style={{ opacity: 1 }}>
                        <button title={p.featured ? 'Retirer vedette' : 'Mettre vedette'} onClick={() => onToggleFeatured(p.id, p.featured)}>★</button>
                        <button title="Éditer" onClick={() => openEdit(p.id)}>✎</button>
                        <button title="Supprimer" className="danger" onClick={() => onDelete(p.id, p.title)}>✕</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="table-foot">
              <span>{total} cliché{total > 1 ? 's' : ''}</span>
              <span>{items.length} affiché{items.length > 1 ? 's' : ''}</span>
            </div>
          </div>
        )}
      </div>

      {/* ============ MODALE ============ */}
      {open && (
        <div className="admin-modal-bg" onClick={close}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-head">
              <div>
                <span className="admin-modal-eyebrow">{editingId ? 'Éditer la photo' : 'Nouvelle photo'}</span>
                <h2 className="admin-modal-title">{editingId ? form.title || '—' : 'Ajouter un cliché'}</h2>
              </div>
              <button onClick={close} className="admin-modal-close" aria-label="Fermer">✕</button>
            </div>

            {/* Aperçu */}
            {(form.imageSeed || form.imageUrl) && (
              <div style={{
                width: '100%', aspectRatio: '5 / 3',
                borderRadius: 12, marginBottom: 16,
                backgroundImage: `url('${form.imageUrl || `https://picsum.photos/seed/${form.imageSeed}/900/540`}')`,
                backgroundSize: 'cover', backgroundPosition: 'center',
                background: '#222',
              }} />
            )}

            <form onSubmit={onSubmit} className="admin-form">
              <Field label="Titre / caption">
                <input
                  type="text"
                  required
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="Ex : Pirogues à l'aube · Plage Nord"
                />
              </Field>

              <Field label="Description (optionnel)">
                <textarea
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Contexte, conditions de prise, anecdote"
                />
              </Field>

              <div className="admin-form-row" style={{ gridTemplateColumns: '1.5fr 1fr' }}>
                <Field label="Catégorie">
                  <select
                    required
                    value={form.category}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  >
                    {PHOTO_CATEGORIES.filter((c) => c.id !== 'all').map((c) =>
                      <option key={c.id} value={c.id}>{c.lbl}</option>
                    )}
                  </select>
                </Field>
                <Field label="Taille (masonry)">
                  <select
                    value={form.size}
                    onChange={(e) => setForm((f) => ({ ...f, size: e.target.value }))}
                  >
                    {PHOTO_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
              </div>

              <div className="admin-form-row">
                <Field label="Auteur · photographe">
                  <input
                    type="text"
                    value={form.author}
                    onChange={(e) => setForm((f) => ({ ...f, author: e.target.value }))}
                    placeholder="Mor Sène"
                  />
                </Field>
                <Field label="Collection (optionnel)">
                  <input
                    type="text"
                    value={form.collection}
                    onChange={(e) => setForm((f) => ({ ...f, collection: e.target.value }))}
                    placeholder="Golden hour"
                  />
                </Field>
              </div>

              <Field label="Image · seed Picsum ou URL">
                <input
                  type="text"
                  required
                  value={form.imageSeed || form.imageUrl}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (/^https?:\/\//.test(v)) setForm((f) => ({ ...f, imageUrl: v, imageSeed: '' }));
                    else setForm((f) => ({ ...f, imageSeed: v, imageUrl: '' }));
                  }}
                  placeholder="cb-gal-mon-cliche  ·  ou  https://…"
                />
              </Field>

              <div className="admin-form-row">
                <label className="admin-publish-toggle">
                  <input
                    type="checkbox"
                    checked={form.featured}
                    onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))}
                  />
                  <span>★ Vedette / mis en avant</span>
                </label>
                <label className="admin-publish-toggle">
                  <input
                    type="checkbox"
                    checked={form.published}
                    onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))}
                  />
                  <span>{form.published ? 'Visible sur /galerie' : 'Caché (brouillon)'}</span>
                </label>
              </div>

              {saveError && <div className="admin-form-error">{saveError}</div>}

              <div className="admin-form-foot">
                <button type="button" onClick={close} className="admin-btn">Annuler</button>
                <button type="submit" disabled={saving} className="admin-btn admin-btn-primary">
                  {saving ? 'Enregistrement…' : (editingId ? 'Enregistrer' : 'Ajouter')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

function Field({ label, children }) {
  return (
    <label className="admin-field">
      <span className="admin-field-label">{label}</span>
      {children}
    </label>
  );
}

export default Galerie;
